import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { InputText } from 'primereact/inputtext'
import Button from '../../components/ui/Button'
import { useToast } from '../../components/ui/ToastProvider'
import ConfirmDialog, { confirmDialog } from '../../components/ui/ConfirmDialog'
import EmptyState from '../../components/ui/EmptyState'
import PageHeader from '../../components/ui/PageHeader'
import CompletedSection from './components/CompletedSection'
import TodoTaskCommentDialog from './components/TodoTaskCommentDialog'
import TodoTaskFilesDialog from './components/TodoTaskFilesDialog'
import TodoTaskRow from './components/TodoTaskRow'
import { useTodoLists, useTodoTasks } from './hooks/useTodoLists'
import { useProxyMode } from '../proxy/useProxyMode'
import { TODO_TASK_STATUS, type TodoTask } from './types'
import './todo.css'
import '../admin/admin.css'

/**
 * <b>Notlarım → liste detayı</b> (113): aktif maddeler + açılır/kapanır "Tamamlananlar".
 *
 * <p>Liste bilgisi ayrı bir uçtan değil, <b>liste sorgusundan</b> okunur: ana ekran zaten o veriyi
 * çekiyor ve detay ondan sonra açılıyor. Böylece ikinci bir uç ve ikinci bir yetki kontrolü
 * gerekmiyor; kullanıcı doğrudan adresle gelse bile liste sorgusu yalnız görebildiklerini döndürdüğü
 * için ekran boş kalır (sunucu ayrıca 404 verir).
 *
 * <p><b>Sahipliğe bağlı salt-okuma KALKTI</b> (30.09.2026): listeyi gören herkes maddeleri ekleyip
 * tamamlayabildiği ve silebildiği için "Yeni Görev Ekle" alanı ve satır aksiyonları {@code owned}'a
 * bakmaz. {@code owned} yalnız "Liste sahibi: …" satırını çizmek için okunur — bir yetki bayrağı
 * değildir.
 *
 * <p><b>Tek istisna: Kısıtlı Yetkili asistan</b> (126, 21.10.2026). Ölçüt <b>sahiplik ya da yetki
 * değil, persona kipidir</b> ({@code useProxyMode().readOnly}) — yani yukarıdaki cümleyle çelişmez,
 * ona dik bir eksen ekler. O kipte ekleme satırı hiç çizilmez, tamamlama kutusu pasifleşir ve silme
 * düşer; sunucu da zaten GET dışı her isteği reddeder. Bu modülde yazmanın <b>ayrı bir yetki kodu
 * yok</b>, dolayısıyla yetki daraltması bu yüzeyleri kapatamaz — koruma yalnız buradadır, bu yüzden
 * yeni bir yazma yüzeyi eklerken {@code readOnly} sorulmalıdır.
 */
export default function TodoListDetailPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { listId } = useParams()
  const id = Number(listId)

  const { list: lists } = useTodoLists()
  const { list, create, changeStatus, remove, updateDescription } = useTodoTasks(id)
  /**
   * Kısıtlı Yetkili asistan bu ekranı **görür ama değiştiremez** (kullanıcı bildirimi 21.10.2026).
   * Düşen yüzeyler: madde ekleme satırı, tamamlama kutusu, silme, yorum kaydetme ve dosya
   * yükleme/silme. Kalanlar okuma: madde listesi, yorum metni, dosya listesi ve indirme.
   */
  const { readOnly } = useProxyMode()
  const toast = useToast()

  const [draft, setDraft] = useState('')
  // 123 — Dosyalar pop-up'ı açık madde. Kimlikle tutulur: ek sonrası liste tazelenince (ek göstergesi
  // değişir) güncel madde nesnesi aşağıda listeden yeniden okunur.
  const [filesTaskId, setFilesTaskId] = useState<number | null>(null)
  // 124 — Yorum pop-up'ı açık madde; aynı gerekçeyle kimlikle tutulur (409 sonrası güncel modDate).
  const [commentTaskId, setCommentTaskId] = useState<number | null>(null)

  const todoList = (lists.data ?? []).find((l) => l.id === id)
  const tasks = list.data ?? []
  const active = tasks.filter((x) => x.todoTaskStatusId !== TODO_TASK_STATUS.COMPLETED)
  const completed = tasks.filter((x) => x.todoTaskStatusId === TODO_TASK_STATUS.COMPLETED)
  const busyId = remove.isPending ? remove.variables : null
  const filesTask = tasks.find((x) => x.id === filesTaskId) ?? null
  const commentTask = tasks.find((x) => x.id === commentTaskId) ?? null

  /**
   * Görev ekler. **Süren bir istek varken hiçbir şey yapmaz** — THY inceleme bulgusu
   * (25.09.2026, `ceo-frontend` teslim PR'ı).
   *
   * <p>Düğme `create.isPending` ile pasifleşiyordu, ama Enter tuşu handler'ı `addTask`'ı
   * **doğrudan** çağırıyor ve alan açık kalıyordu: hızlı ya da tekrarlanan Enter, aynı görevi
   * birden çok kez oluşturabiliyordu. Kapı düğmede değil, **burada** olmalı — düğmeyi pasif
   * yapmak yalnız bir giriş yolunu kapatır, ikincisini (klavye) açık bırakır.
   */
  const addTask = () => {
    if (create.isPending) return
    const name = draft.trim()
    if (!name) return
    create.mutate({ name }, { onSuccess: () => setDraft('') })
  }

  const toggle = (task: TodoTask, done: boolean) =>
    changeStatus.mutate({
      id: task.id,
      statusId: done ? TODO_TASK_STATUS.COMPLETED : TODO_TASK_STATUS.ACTIVE,
    })

  const confirmDelete = (task: TodoTask) =>
    confirmDialog({
      message: t('todo.deleteTaskConfirm'),
      header: t('todo.deleteTask'),
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      acceptLabel: t('common.delete'),
      rejectLabel: t('common.cancel'),
      accept: () => remove.mutate(task.id),
    })

  /** 124 — yorumu yazar; boş metin kaldırır. 409'da pop-up açık kalır (hata toast'ı merkezî). */
  const saveComment = (description: string) => {
    if (commentTask == null) return
    updateDescription.mutate(
      { task: commentTask, description },
      {
        onSuccess: () => {
          setCommentTaskId(null)
          toast.success(t(description ? 'todo.comment.saved' : 'todo.comment.removed'))
        },
      },
    )
  }

  const row = (task: TodoTask) => (
    <TodoTaskRow
      key={task.id}
      task={task}
      busy={busyId === task.id}
      readOnly={readOnly}
      onToggle={toggle}
      onDelete={confirmDelete}
      onFiles={(x) => setFilesTaskId(x.id)}
      onComment={(x) => setCommentTaskId(x.id)}
    />
  )

  /**
   * 124 — kolon başlıkları (Görev | Yorum); aktif listenin ve açık Tamamlananlar bölümünün üstünde.
   * Yalnız geniş ekranda; telefonda yorum adın altına iner ve başlık hizası anlamını yitirir.
   * aria-hidden: ekran okuyucu satırdaki gizli "Yorum:" önekini okur; tablo semantiği taşımayan bir
   * listede başlık yalnız gürültü olurdu.
   */
  const columnsHead = (
    <div className="todo-task-head" aria-hidden="true">
      <span className="todo-task-head-name">{t('todo.taskName')}</span>
      <span className="todo-task-head-comment">{t('todo.comment.label')}</span>
    </div>
  )

  return (
    <div className="admin-page todo-detail">
      <ConfirmDialog />
      <PageHeader
        title={todoList?.name ?? t('todo.title')}
        /* Geri: tarayıcı geçmişi (`navigate(-1)`) DEĞİL, doğrudan liste yolu. Detay adresi
           paylaşılabilir ve yer imine eklenebilir; geçmişe dayanan bir düğme o durumda
           kullanıcıyı uygulamanın dışına çıkarırdı. */
        actions={
          <Button
            variant="secondary"
            size="small"
            icon="pi pi-arrow-left"
            label={t('todo.backToLists')}
            onClick={() => navigate('/minutes/todo-lists')}
          />
        }
      />

      {!todoList?.owned && todoList?.ownerFullName && (
        <p className="todo-detail-owner">
          {t('todo.ownerPrefix')}: {todoList.ownerFullName}
        </p>
      )}

      {/* Ekleme satırı maddelerin ÜSTÜNDE (kullanıcı kararı 25.09.2026). Analist metni konumu
          "alt bölümde ya da tasarıma uygun sabit alanda" diye serbest bırakmıştı; üstte olması uzun
          listelerde alana ulaşmak için sona kaydırmayı ortadan kaldırıyor ve yeni eklenen madde
          zaten listenin sonuna gittiği için imleç ile sonucun ayrışması bir maliyet değil. */}
      {/* Ekleme satırı Kısıtlı Yetkide HİÇ çizilmez: pasif bir alan + pasif bir düğme, kullanıcıya
          "burada bir şey eksik" dedirtirdi — oysa eksik olan bir şey yok, yetki yok. */}
      {!readOnly && (
        <div className="todo-add-row">
          <InputText
            value={draft}
            maxLength={1000}
            placeholder={t('todo.taskPlaceholder')}
            /* Giriş alanı ile düğme AYNI adı taşımamalı: sesli kontrol kullanıcısı "Yeni Görev
             Ekle" dediğinde hangisinin kastedildiği belirsiz kalırdı. Alan "Görev", düğme
             "Yeni Görev Ekle". */
            aria-label={t('todo.taskName')}
            onChange={(e) => setDraft(e.target.value)}
            /* Enter ile ekleme: liste tutmanın doğal ritmi budur; her madde için fareye gitmek
             akışı kırardı. Düğme de durur — klavyesiz (dokunmatik) kullanım için gerekli. */
            onKeyDown={(e) => {
              if (e.key === 'Enter') addTask()
            }}
            /* Alan da kilitlenir: yalnız `addTask` içindeki kapı yeterli olurdu ama
             kullanıcı istek sürerken yazmaya devam edip "neden eklenmedi" sanardı. */
            disabled={create.isPending}
          />
          <Button
            icon="pi pi-plus"
            label={t('todo.newTask')}
            disabled={create.isPending || draft.trim().length === 0}
            onClick={addTask}
          />
        </div>
      )}

      {/* Açıklama salt-okumada DÜŞER: metin bir EYLEM ÇAĞRISIDIR ("… ile oluşturun") ve o
          kipte çağırdığı düğme çizilmiyor. Başlık bilgiyi zaten taşıyor, yani düşürmekle bir şey
          kaybedilmiyor — bırakmak, olmayan bir yüzeyi tarif ederek tam da FR-019'un kapatmaya
          çalıştığı yanılgıyı üretirdi (push kapısı review'ü 21.10.2026). */}
      {!list.isLoading && tasks.length === 0 && (
        <EmptyState
          title={t('todo.emptyTasksTitle')}
          description={readOnly ? undefined : t('todo.emptyTasksDesc')}
        />
      )}

      {/* Yalnız aktif madde varken: hepsi tamamlanınca başlık boş listenin üstünde asılı kalırdı. */}
      {active.length > 0 && columnsHead}

      <ul className="todo-task-list">{active.map(row)}</ul>

      {/* Tamamlananlar açılınca aynı başlık orada da çizilir (kullanıcı isteği 14.10.2026): bölüm
          aktif listeden ayraçla ayrıldığı için yukarıdaki başlık o maddeleri artık etiketlemiyordu. */}
      <CompletedSection count={completed.length} head={columnsHead}>
        {completed.map(row)}
      </CompletedSection>

      <TodoTaskFilesDialog
        task={filesTask}
        listId={id}
        readOnly={readOnly}
        onHide={() => setFilesTaskId(null)}
      />

      <TodoTaskCommentDialog
        task={commentTask}
        readOnly={readOnly}
        submitting={updateDescription.isPending}
        onSubmit={saveComment}
        onHide={() => setCommentTaskId(null)}
      />
    </div>
  )
}
